/* eslint-disable max-len */
import type { TefComsCustomerAttribute } from '@mfe/components/tef-coms-customer-attribute';
import { CustomerAttribute, CustomerAttributeGroupItem } from '@mfec/customerAttributes/types';
import { html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { TefComsCustomerAttributeBase } from '@mfec/customerAttributes/TefComsCustomerAttributeBase';
import { localDate, localTime } from '../util';

export function getCustomerAttributesWithBulletpointTemplateResult(customerAttribute: CustomerAttribute, type: 'standard' | 'extra') {
  return customerAttribute[type].items.map((item: CustomerAttributeGroupItem) => html`
    <one-list-item>${unsafeHTML(item.title)}</one-list-item>
  `);
}

export function getCustomerAttributesWithIconTemplateResult(customerAttribute: CustomerAttribute) {
  return customerAttribute.extra.items.filter((item) => item.display).map((item: CustomerAttributeGroupItem) => html`
    <one-list-item>
      <one-icon slot="icon" name="${item.granted ? 'check' : 'cancel'}" variant="outline" size="xs"></one-icon>
      ${unsafeHTML(item.title)}
    </one-list-item>
  `);
}

export function getCustomerAttributesWithCheckboxTemplateResult(tefComsCustomerAttribute: TefComsCustomerAttributeBase, customerAttribute: CustomerAttribute) {
  return customerAttribute.extra.items.map((item: CustomerAttributeGroupItem) => html`
    <one-group direction="row" gap="xs" wrap="noWrap" align-items="flex-start">
      <one-checkbox .checked=${item.granted} data-cattr-id="${customerAttribute.id}" data-cattr-item-id="${item.id}" @click=${() => { tefComsCustomerAttribute.save(); }}></one-checkbox>
      <one-text as="span">${unsafeHTML(item.title)}</one-text>
    </one-group>
  `);
}

export function getExtraCustomerAttributeItemsTemplateResult(tefComsCustomerAttribute: TefComsCustomerAttributeBase, customerAttribute: CustomerAttribute, itemsDisplayStyle: string) {
  switch (itemsDisplayStyle) {
    case 'bulletpoint':
      return html`
        <one-list icon="bullet-ul-list" icon-variant="solid">
          ${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'extra')}
        </one-list>
      `;
    case 'icon':
      return html`<one-list>${getCustomerAttributesWithIconTemplateResult(customerAttribute)}</one-list>`;
    case 'checkbox':
      return html`${getCustomerAttributesWithCheckboxTemplateResult(tefComsCustomerAttribute, customerAttribute)}`;
    default:
      return null;
  }
}

export function getLastChangeTemplateResult(customerAttribute: CustomerAttribute, itemsDisplayStyle: string) {
  if (itemsDisplayStyle !== 'bulletpoint') {
    return html`
      <one-list-item size="xs" align-items="center">
        <one-icon name="clock" variant="outline" slot="icon" size="xs"></one-icon>
        <one-content>
          <one-text size="xs"> 
            Letzte Änderung am ${localDate(customerAttribute.date)} um ${localTime(customerAttribute.date)} Uhr
          </one-text>
        </one-content>
      </one-list-item>
    `;
  } return null;
}

export function getButtonsTemplateResult(tefComsCustomerAttribute: TefComsCustomerAttributeBase, mode: string) {
  const hideModal = () => {
    const modal : HTMLElement & { hide?: () => void } = document.querySelector('tef-coms').shadowRoot.getElementById(`tef-coms-customer-attribute-modal-${tefComsCustomerAttribute.id}`);
    modal.hide();
  };
  if (mode === 'checkbox') {
    return html`
      <one-button size="small" color="default" variant="solid" @click=${() => { tefComsCustomerAttribute.save(); hideModal(); }}>
        Übernehmen
      </one-button>
      <one-spacer size="fill"></one-spacer>
      <one-button size="small" color="default" variant="solid" @click=${() => { tefComsCustomerAttribute.cancel(); hideModal(); }}>
        Abbrechen
      </one-button>
    `;
  } return html`
    <one-button size="small" color="default" variant="solid" @click=${hideModal}>
      Zurück
    </one-button>
  `;
}

export const tefComsCustomerAttributeModalDialog = function render(
  this: TefComsCustomerAttributeBase,
  customerAttribute: CustomerAttribute,
  itemsDisplayStyle: string,
  template$: HTMLTemplateElement,
) {
  // For whatever reason the <template> doesn't render correctly with tefOne.
  // Thus we must fake the main usecase: O2
  const title = (template$.content.textContent || '').replace('O2', 'O<sub>2</sub>');

  return html`
  <one-modal id="tef-coms-customer-attribute-modal-${this.id}" closable="" size="md" variant="default">
    <span slot="label">${unsafeHTML(title)}</span>
    <one-stack gap="md">
      <one-text strong>${unsafeHTML(customerAttribute.standard.title || 'Standardprodukte')}</one-text>
      <one-list icon="bullet-ul-list" icon-variant="solid">
        ${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'standard')}
      </one-list>
      <one-text strong>${unsafeHTML(customerAttribute.extra.title || 'Zusatzprodukte')}</one-text>
      ${getExtraCustomerAttributeItemsTemplateResult(this, customerAttribute, itemsDisplayStyle)}
      ${getLastChangeTemplateResult(customerAttribute, itemsDisplayStyle)}
      <one-text as="p">${unsafeHTML(customerAttribute.footer)}</one-text>
    </one-stack>
    <one-button-group slot="footer">${getButtonsTemplateResult(this, itemsDisplayStyle)}</one-button-group>
  </one-modal>
  `;
};

export const tefComsCustomerAttributeModalTrigger = function render(
  this: TefComsCustomerAttribute,
  template$: HTMLTemplateElement,
) {
  const showModal = () => {
    const modal : HTMLElement & { show?: () => void } = document.querySelector('tef-coms').shadowRoot.getElementById(`tef-coms-customer-attribute-modal-${this.id}`);
    modal.show();
  };
  return html`<one-link as="span" color="prominent" id="tef-coms-customer-attribute-${this.id}" @click=${showModal}>${template$.content}</one-link>`;
};
